import { defineStore } from 'pinia'

import * as fuelFormApi from '../services/api/fuel-form'

export interface IInvoiceData {
  note: string
  amount?: number
  unit?: string
  _amount?: string
}

export interface ICompletion {
  priority: number
  user_id: number
  role: string
  is_completed: boolean
  reason: string
  completed_at: string
}

export interface IFuelFormData {
  id: number
  status: string
  is_urgent: boolean
  note: string
  completion_note: string
  reason: string
  approvals: Array<IApproval>
  completions: Array<ICompletion>
  invoices: Array<IInvoiceData>
  images: Array<any>
  completion_images: Array<any>
  assignee_id: number
  car_id: number
  completer_id: number
  assignee: any
  car: any
  created_at: string
}

export interface IApproval {
  id?: number
  user_id?: number
  role?: string
  is_approved?: boolean
  reason?: string
  approved_at?: string
}

export const useFuelFormStore = defineStore('fuelForm', {
  state: () => ({
    fuelForm: {
      id: 0,
      status: '',
      is_urgent: false,
      note: '',
      completion_note: '',
      reason: '',
      approvals: [],
      completions: [],
      invoices: [],
      images: [],
      completion_images: [],
      assignee_id: 0,
      car_id: 0,
      completer_id: 0,
      assignee: {},
      car: {},
      created_at: '',
    } as IFuelFormData,
    isLoading: false,
  }),

  actions: {
    setFuelForm(data: IFuelFormData) {
      this.fuelForm = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getFuelForm(id: number) {
      try {
        const { data } = await fuelFormApi.get(id)
        this.setFuelForm(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async updateFuelForm(payload: any) {
      try {
        const { data } = await fuelFormApi.update(this.fuelForm.id, payload)
        this.setFuelForm(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async applyFuelForm() {
      try {
        const { data } = await fuelFormApi.apply(this.fuelForm.id)
        this.setFuelForm(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async rejectFuelForm(payload: any) {
      try {
        const { data } = await fuelFormApi.reject(this.fuelForm.id, payload)
        this.setFuelForm(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async remakeFuelForm(payload: any) {
      try {
        const { data } = await fuelFormApi.remake(this.fuelForm.id, payload)
        this.setFuelForm(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async approveFuelForm(payload: any) {
      try {
        const { data } = await fuelFormApi.approve(this.fuelForm.id, payload)
        this.setFuelForm(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async completeFuelForm(payload: any) {
      try {
        const { data } = await fuelFormApi.complete(this.fuelForm.id, payload)
        this.setFuelForm(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },
  },
})

export const useFuelFormListStore = defineStore('fuelFormList', {
  state: () => ({
    fuelForms: [] as IFuelFormData[],
    count: 0,
    isLoading: false,
    total: 0,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    addFuelForm(data: IFuelFormData) {
      this.fuelForms.push(data)
    },

    setFuelForms(data: IFuelFormData[]) {
      this.fuelForms = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    setTotal(value: number) {
      this.total = value
    },

    async countFuelForms() {
      try {
        const { data } = await fuelFormApi.count()
        this.setCount(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async createFuelForm(payload: any) {
      try {
        const { data } = await fuelFormApi.create(payload)
        this.addFuelForm(data)
        return data
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async listFuelForms(params: any) {
      try {
        this.setLoading(true)
        const { data, total } = (await fuelFormApi.list(params)) as any
        this.setFuelForms(data)
        this.setTotal(total)
        this.setLoading(false)
        return { data, total }
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllFuelForms(params: any) {
      try {
        this.setLoading(true)
        const { data, total } = (await fuelFormApi.listAll(params)) as any
        this.setFuelForms(data)
        this.setTotal(total)
        this.setLoading(false)
        return { data, total }
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
